import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, Locate, CheckCircle, ShoppingCart, CreditCard, FileText, AlertTriangle, Timer, X, ChevronLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import { getActiveVisit, saveActiveVisit, clearActiveVisit } from '@/lib/useActiveVisit';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { api } from '@/api/apiClient';
import { Visit } from '@/api/localStore';

// Haversine distance formula
function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

const OUTCOMES = [
  { key: 'order', label: '🛒 تم البيع', desc: 'تم أخذ طلب' },
  { key: 'collection', label: '💰 تم التحصيل', desc: 'تم استلام مبلغ' },
  { key: 'order_collection', label: '🛒💰 تم البيع والتحصيل', desc: 'طلب + تحصيل في نفس الزيارة' },
  { key: 'info_only', label: '📋 معلومات', desc: 'معلومات / استبيان' },
  { key: 'no_sale', label: '❌ لم يتم البيع', desc: 'بدون نتيجة' },
  { key: 'not_available', label: '⚠️ غير موجود', desc: 'العميل غير موجود' },
];

const NO_SALE_REASONS = ['العميل غير موجود', 'لا احتياج حالياً', 'مشكلة في السعر', 'مخزون كافي', 'العميل غاضب', 'أخرى'];

const GEOFENCE_RADIUS = 300; // meters (relaxed for field use)

// Skeleton card for loading state
function CustomerSkeleton() {
  return (
    <div className="bg-card border border-border rounded-xl p-3 flex gap-3 animate-pulse">
      <div className="w-10 h-10 bg-muted rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2 py-0.5">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
      </div>
    </div>
  );
}

// STEP 1: Select customer — uses EXACT same logic as /customers page
function StepSelectCustomer({ onSelect, preCustomerId, preCustomerName, salesmanUid }) {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const searchTimerRef = useRef(null);
  const PAGE_SIZE = 50;

  const fetchPage = useCallback(async (pg, q, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    const term = q?.trim();
    const params = { search: term || undefined, limit: PAGE_SIZE, page: pg, sort_by: 'name_asc' };
    // '/sellgo_partners' actually filters by the search term (the legacy
    // '/customers' ignores `search`, so typing a name returned an unfiltered
    // list). Fall back to '/customers' only if the SellGo endpoint errors.
    let res;
    try {
      res = await api.get('/sellgo_v5_customer_search', params);
    } catch {
      res = await api.get('/customers', params);
    }
    if (res?.success) {
      const newData = res.data || [];
      setCustomers(prev => append ? [...prev, ...newData] : newData);
      setTotal(res.total || 0);
    }
    if (append) setLoadingMore(false);
    else setLoading(false);
  }, []);

  // Auto-select if pre-filled
  useEffect(() => {
    if (preCustomerId) {
      onSelect({ id: parseInt(preCustomerId), name: preCustomerName || '' });
      return;
    }
    fetchPage(0, '');
  }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchPage(0, val);
    }, 350);
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, search, true);
  };

  const hasMore = (page + 1) < Math.ceil(total / PAGE_SIZE);

  if (preCustomerId) return null;

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={e => handleSearch(e.target.value)}
          placeholder="بحث بالاسم أو الهاتف أو المدينة..."
          className="pr-10 rounded-xl" />
        {search && (
          <button onClick={() => handleSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Count */}
      {!loading && total > 0 && (
        <p className="text-xs text-muted-foreground px-1">
          {total.toLocaleString('ar-EG')} عميل
        </p>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <CustomerSkeleton key={i} />)}</div>
      ) : customers.length === 0 ? (
        <p className="text-center text-muted-foreground py-8 text-sm">
          {search ? `لا توجد نتائج لـ "${search}"` : 'لا يوجد عملاء'}
        </p>
      ) : (
        <div className="space-y-2">
          {customers.map(c => (
            <div key={c.id} onClick={() => onSelect(c)}
              className="bg-card border border-border rounded-xl p-3 flex items-center gap-3 cursor-pointer active:scale-[.98] hover:border-primary/40 transition-all">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-black">{c.name?.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-foreground truncate">{c.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {c.phone && <span className="text-xs text-muted-foreground">{c.phone}</span>}
                  {c.city && <span className="text-xs text-muted-foreground">• {c.city}</span>}
                </div>
              </div>
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </div>
          ))}

          {hasMore && (
            <button onClick={loadMore} disabled={loadingMore}
              className="w-full py-3 text-sm text-primary font-semibold rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-50">
              {loadingMore ? 'جاري التحميل...' : `تحميل المزيد (${total - customers.length} متبقي)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// STEP 2: GPS Check-in
function StepCheckIn({ customer, onCheckIn }) {
  const [location, setLocation] = useState(null);
  const [getting, setGetting] = useState(false);
  const [gpsWarning, setGpsWarning] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [manualAddress, setManualAddress] = useState('');

  // Prefill the arrival address from the customer's Odoo contact (best-effort).
  // Selected-from-search customers carry street/city; planned visits carry only
  // id+name, so we look the address up by name and match on id.
  useEffect(() => {
    let cancelled = false;
    const build = (c) => [c?.street, c?.street2, c?.city].filter(Boolean).join('، ').trim();
    const local = build(customer);
    if (local) { setManualAddress(prev => prev || local); return; }
    if (customer?.id && customer?.name) {
      (async () => {
        try {
          const res = await api.get('/sellgo_v5_customer_search', { search: customer.name, limit: 5 });
          const list = res?.data || res || [];
          const match = Array.isArray(list) ? list.find(x => x.id === customer.id) : null;
          const addr = build(match);
          if (!cancelled && addr) setManualAddress(prev => prev || addr);
        } catch { /* best effort — address stays empty, it's optional */ }
      })();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  const getGPS = () => {
    setGetting(true);
    setGpsError(null);
    if (!navigator.geolocation) {
      setGpsError('GPS غير متاح على هذا الجهاز');
      setGetting(false);
      return;
    }

    let best = null;
    let watchId = null;
    let hardStop = null;

    const apply = () => {
      if (watchId !== null) { navigator.geolocation.clearWatch(watchId); watchId = null; }
      if (hardStop) { clearTimeout(hardStop); hardStop = null; }
      setGetting(false);
      if (!best) return;
      const loc = { lat: best.coords.latitude, lng: best.coords.longitude, accuracy: Math.round(best.coords.accuracy) };
      setLocation(loc);
      if (customer.partner_latitude && customer.partner_longitude) {
        const dist = haversineMeters(loc.lat, loc.lng, customer.partner_latitude, customer.partner_longitude);
        setGpsWarning(dist > GEOFENCE_RADIUS ? { distance: dist } : null);
      }
    };

    // Collect readings for up to 12s and keep the most accurate one.
    hardStop = setTimeout(apply, 12000);
    watchId = navigator.geolocation.watchPosition(
      pos => {
        if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
        // Good enough → stop early (accuracy in meters).
        if (pos.coords.accuracy <= 20) apply();
      },
      (err) => {
        // If we already have a fix, just use the best we got.
        if (best) { apply(); return; }
        if (watchId !== null) { navigator.geolocation.clearWatch(watchId); watchId = null; }
        if (hardStop) { clearTimeout(hardStop); hardStop = null; }
        let msg = 'تعذر تحديد الموقع';
        if (err && err.code === 1) {
          msg = 'تم رفض إذن الموقع. تأكد من السماح للموقع، أو افتح الموقع على https أو localhost.';
        } else if (err && err.code === 2) {
          msg = 'الموقع غير متاح حاليًا. تأكد من تفعيل GPS.';
        } else if (err && err.code === 3) {
          msg = 'انتهت مهلة تحديد الموقع. حاول مجددًا.';
        }
        setGpsError(msg);
        setGetting(false);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
  };

  const handleCheckIn = () => {
    onCheckIn({
      location,
      gps_verified: !!location && !gpsWarning,
      warned: !!gpsWarning,
      skipped_gps: !location,
      manual_address: manualAddress.trim() || null,
    });
  };

  return (
    <div className="space-y-4">
      {/* Customer card */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-center gap-3">
        <div className="w-12 h-12 bg-primary/15 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-black text-lg">{customer.name?.charAt(0)}</span>
        </div>
        <div>
          <p className="font-bold text-foreground">{customer.name}</p>
          {customer.city && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{customer.city}</p>}
        </div>
      </div>

      {/* GPS button */}
      <Button onClick={getGPS} disabled={getting} variant="outline" className="w-full h-14 rounded-2xl gap-3 border-2 border-dashed border-border text-base">
        <Locate className={`w-5 h-5 ${getting ? 'animate-pulse' : ''} text-primary`} />
        {getting ? 'جاري تحديد الموقع بدقة...' : location
          ? <span>✅ الموقع محدد ({location.accuracy}م دقة)</span>
          : 'تحديد موقعي الحالي'}
      </Button>

      {/* GPS error */}
      {gpsError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-700">تعذر الوصول إلى الموقع</p>
            <p className="text-xs text-red-600 mt-0.5">{gpsError}</p>
            <p className="text-xs text-muted-foreground mt-1">
              نصيحة: لو الموقع شغّال على HTTP، استخدم العنوان <span className="font-mono">http://localhost:5173</span> بدل عنوان الـ IP.
            </p>
          </div>
        </div>
      )}

      {/* Geofence warning */}
      {gpsWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-700">تنبيه: أنت بعيد عن موقع العميل</p>
            <p className="text-xs text-amber-600 mt-0.5">المسافة المقاسة: {gpsWarning.distance} متر</p>
            <p className="text-xs text-amber-600">هل أنت بالفعل عند العميل؟</p>
          </div>
        </div>
      )}

      {/* Manual address — optional (auto-filled from the customer's contact) */}
      <div>
        <p className="text-xs text-muted-foreground mb-1.5">عنوان الوصول (اختياري)</p>
        <Textarea value={manualAddress} onChange={e => setManualAddress(e.target.value)}
          placeholder="مثال: شارع المحطة، بجوار الصيدلية..." rows={2} className="rounded-xl" />
      </div>

      {/* Check-in — needs GPS only; the address is optional */}
      <Button onClick={handleCheckIn} disabled={!location}
        className="w-full h-16 bg-primary text-white text-lg font-bold rounded-2xl gap-3 shadow-lg shadow-primary/20 disabled:opacity-50">
        <MapPin className="w-6 h-6" />📍 تسجيل الوصول
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        {!location
          ? 'حدّد موقعك بالـGPS عشان تسجّل الوصول'
          : `إحداثيات: ${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`}
      </p>
    </div>
  );
}

// STEP 3: Active visit
function StepActiveVisit({ customer, checkInData, visitId, onEndVisit, onAction, activeStartTime }) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(activeStartTime || Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const fmtTimer = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const ACTIONS = [
    { key: 'order', icon: ShoppingCart, label: 'أخذ طلب', color: 'bg-blue-50 border-blue-200 text-blue-700' },
    { key: 'collection', icon: CreditCard, label: 'تسجيل تحصيل', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
    { key: 'notes', icon: FileText, label: 'ملاحظات', color: 'bg-amber-50 border-amber-200 text-amber-700' },
    { key: 'not_available', icon: AlertTriangle, label: 'مشكلة/شكوى', color: 'bg-red-50 border-red-200 text-red-600' },
  ];

  return (
    <div className="space-y-4">
      {/* Live timer banner */}
      <div className="bg-primary rounded-2xl p-4 flex items-center justify-between">
        <div>
          <p className="text-white/70 text-xs">زيارة جارية</p>
          <p className="text-white font-bold">{customer.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Timer className="w-4 h-4 text-white/70" />
          <span className="text-2xl font-black text-white font-mono">{fmtTimer(elapsed)}</span>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        {ACTIONS.map(a => (
          <button key={a.key} onClick={() => onAction(a.key, customer, elapsed)}
            className={`flex items-center gap-2.5 p-4 rounded-2xl border-2 text-sm font-bold transition-all active:scale-[.97] ${a.color}`}>
            <a.icon className="w-5 h-5 flex-shrink-0" />
            {a.label}
          </button>
        ))}
      </div>

      {/* End visit button */}
      <Button onClick={() => onEndVisit(elapsed)} className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white text-base font-bold rounded-2xl gap-2 shadow-lg">
        <CheckCircle className="w-5 h-5" /> إنهاء الزيارة
      </Button>
    </div>
  );
}

// STEP 4: Check out
function StepCheckOut({ customer, duration, onConfirm, saving }) {
  const [outcome, setOutcome] = useState('no_sale');
  const [noSaleReason, setNoSaleReason] = useState('');
  const [notes, setNotes] = useState('');

  const mins = Math.floor(duration / 60);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          <p className="font-bold text-emerald-800">ملخص الزيارة</p>
        </div>
        <p className="text-sm text-emerald-700">العميل: <strong>{customer.name}</strong></p>
        <p className="text-sm text-emerald-700">المدة: <strong>{mins} دقيقة</strong></p>
      </div>

      {/* Outcome selection */}
      <div>
        <p className="font-bold text-foreground mb-2 text-sm">نتيجة الزيارة (مطلوب)</p>
        <div className="space-y-2">
          {OUTCOMES.map(o => (
            <button key={o.key} onClick={() => setOutcome(o.key)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-right ${outcome === o.key ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}>
              <div className="flex-1">
                <p className="font-semibold text-sm text-foreground">{o.label}</p>
                <p className="text-xs text-muted-foreground">{o.desc}</p>
              </div>
              {outcome === o.key && <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />}
            </button>
          ))}
        </div>
      </div>

      {/* No-sale reason */}
      {(outcome === 'no_sale' || outcome === 'not_available') && (
        <div>
          <p className="font-semibold text-sm text-foreground mb-2">سبب عدم البيع</p>
          <div className="grid grid-cols-2 gap-2">
            {NO_SALE_REASONS.map(r => (
              <button key={r} onClick={() => setNoSaleReason(r)}
                className={`p-2.5 rounded-xl text-xs font-medium border transition-all text-right ${noSaleReason === r ? 'border-primary bg-primary/5 text-primary' : 'border-border bg-card text-muted-foreground'}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      <div>
        <p className="font-semibold text-sm text-foreground mb-2">ملاحظات (اختياري)</p>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="أي ملاحظات عن الزيارة..." rows={3} className="rounded-xl text-sm" />
      </div>

      {/* Quick tags */}
      <div className="flex flex-wrap gap-2">
        {['عميل راضٍ', 'نفاد منتج', 'منافسة شرسة', 'يحتاج متابعة'].map(tag => (
          <button key={tag} onClick={() => setNotes(n => n ? n + '، ' + tag : tag)}
            className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-full hover:bg-muted/70 transition-colors">
            {tag}
          </button>
        ))}
      </div>

      {/* Confirm */}
      <Button onClick={() => onConfirm({ outcome, no_sale_reason: noSaleReason, notes })}
        disabled={saving} className="w-full h-14 bg-primary text-white text-base font-bold rounded-2xl gap-2">
        {saving ? 'جاري الحفظ...' : <><CheckCircle className="w-5 h-5" /> تأكيد إنهاء الزيارة</>}
      </Button>
    </div>
  );
}

// MAIN PAGE
const STEPS = ['اختيار العميل', 'تسجيل الوصول', 'زيارة جارية', 'إنهاء الزيارة'];

export default function NewVisit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { odooSession } = useOdooAuth();
  const preCustomerId = searchParams.get('customer_id');
  const preCustomerName = searchParams.get('customer_name');
  const preVisitId = searchParams.get('visit_id');

  // Restore from localStorage on mount
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(null);
  const [checkInData, setCheckInData] = useState(null);
  const [visitId, setVisitId] = useState(null);
  // When starting an existing PLANNED visit, we check in on it instead of
  // creating a brand-new one (otherwise the planned card stays + a duplicate appears).
  const [existingVisitId, setExistingVisitId] = useState(preVisitId ? parseInt(preVisitId) : null);
  const [activeStartTime, setActiveStartTime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [savedVisit, setSavedVisit] = useState(null);
  const [restored, setRestored] = useState(false);

  // On mount: restore active visit OR handle pre-filled customer
  useEffect(() => {
    const persisted = getActiveVisit();
    if (persisted && persisted.step >= 3) {
      // Resume active visit
      setCustomer(persisted.customer);
      setCheckInData(persisted.checkInData);
      setVisitId(persisted.visitId);
      setActiveStartTime(persisted.activeStartTime);
      setStep(persisted.step);
    } else if (preCustomerId) {
      setCustomer({ id: parseInt(preCustomerId), name: preCustomerName || '' });
      setStep(2);
    }
    setRestored(true);
  }, []);

  // Persist to localStorage whenever active visit state changes (step 3+)
  useEffect(() => {
    if (!restored) return;
    if (step >= 3 && customer && visitId) {
      saveActiveVisit({ step, customer, checkInData, visitId, activeStartTime });
    }
  }, [step, customer, visitId, activeStartTime, restored]);

  const handleSelectCustomer = (c) => {
    setCustomer(c);
    setStep(2);
  };

  const handleCheckIn = async (data) => {
    setSaving(true);
    let user = null;
    try {
      const meRes = await api.get('/me');
      user = meRes?.data || null;
    } catch {
      // best effort — keep the visit creation working anyway
    }
    const now = new Date().toISOString();
    try {
      let visit;
      if (existingVisitId) {
        // Resume a PLANNED visit → check in on it (no duplicate created).
        const ci = await api.post(`/visits/${existingVisitId}/check-in`, {
          latitude: data.location?.lat ?? null,
          longitude: data.location?.lng ?? null,
          accuracy: data.location?.accuracy ?? null,
          location_address: data.manual_address || null,
        });
        if (!ci?.success) throw new Error(ci?.error || 'فشل تسجيل الوصول');
        visit = ci.data;
      } else {
        visit = await Visit.create({
          customer_id: customer.id,
          customer_name: customer.name,
          salesman_email: user?.email,
          salesman_name: user?.name || user?.full_name,
          visit_date: now,
          check_in_time: now,
          status: 'active',
          notes: '',
          location_address: data.manual_address || '',
          latitude: data.location?.lat,
          longitude: data.location?.lng,
          gps_verified: data.gps_verified,
          gps_accuracy_meters: data.location?.accuracy,
        });
      }
      const startTime = Date.now();
      setVisitId(visit.id);
      // Keep the skipped_gps flag so check-out doesn't try to prompt again
      setCheckInData({ ...data, skipped_gps: data.skipped_gps || !data.location });
      setActiveStartTime(startTime);
      setStep(3);
      saveActiveVisit({
        step: 3, customer,
        checkInData: { ...data, skipped_gps: data.skipped_gps || !data.location },
        visitId: visit.id, activeStartTime: startTime,
      });
      toast.success('تم تسجيل الوصول ✓');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[visit] check-in failed:', e);
      toast.error('فشل تسجيل الوصول: ' + (e?.message || 'خطأ غير معروف'));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = (action, cust) => {
    if (action === 'order') {
      navigate(`/orders/new?customer_id=${cust.id}&customer_name=${encodeURIComponent(cust.name)}`);
    } else if (action === 'collection') {
      navigate(`/collections/new?customer_id=${cust.id}&customer_name=${encodeURIComponent(cust.name)}`);
    }
  };

  const handleEndVisit = () => {
    setStep(4);
    saveActiveVisit({ step: 4, customer, checkInData, visitId, activeStartTime });
  };

  const handleConfirmCheckOut = async ({ outcome, no_sale_reason, notes }) => {
    setSaving(true);
    const now = new Date().toISOString();
    const durationSecs = activeStartTime ? Math.floor((Date.now() - activeStartTime) / 1000) : 0;
    const durationMins = Math.round(durationSecs / 60);

    // Calculate visit score
    let score = 50;
    if (checkInData?.gps_verified) score += 20;
    if (outcome === 'order') score += 20;
    else if (outcome === 'collection') score += 10;
    if (durationMins >= 5 && durationMins <= 45) score += 10;

    // Get checkout GPS — but ONLY if the check-in had GPS too. Otherwise
    // skip it; we don't want to block the user behind a permission prompt
    // they already declined.
    let checkoutLoc = null;
    if (checkInData?.location && !checkInData?.skipped_gps) {
      try {
        checkoutLoc = await new Promise((res) => {
          if (!navigator.geolocation) return res(null);
          let done = false;
          const t = setTimeout(() => { if (!done) { done = true; res(null); } }, 3000);
          navigator.geolocation.getCurrentPosition(
            p => {
              if (done) return;
              done = true;
              clearTimeout(t);
              res({ lat: p.coords.latitude, lng: p.coords.longitude });
            },
            () => { if (!done) { done = true; clearTimeout(t); res(null); } },
            { timeout: 3000 }
          );
        });
      } catch {
        checkoutLoc = null;
      }
    }

    try {
      await Visit.update(visitId, {
        check_out_time: now,
        status: 'completed',
        outcome,
        no_sale_reason,
        notes,
        duration_minutes: durationMins,
        checkout_latitude: checkoutLoc?.lat,
        checkout_longitude: checkoutLoc?.lng,
        visit_score: Math.min(100, score),
      });

      clearActiveVisit();
      setSavedVisit({ outcome, duration_minutes: durationMins, customer_name: customer.name });
      setSuccess(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[visit] checkout failed:', e);
      toast.error('فشل إنهاء الزيارة: ' + (e?.message || 'خطأ غير معروف'));
    } finally {
      setSaving(false);
    }
  };

  // Success screen
  if (success && savedVisit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 gap-6 text-center" dir="rtl">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 12 }}>
          <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center">
            <CheckCircle className="w-14 h-14 text-emerald-500" />
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">تم تسجيل الزيارة!</h2>
          <p className="text-muted-foreground">{savedVisit.customer_name}</p>
          <p className="text-sm text-muted-foreground">{savedVisit.duration_minutes} دقيقة</p>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="grid gap-3 w-full max-w-xs">
          <Button onClick={() => { clearActiveVisit(); navigate('/visits/new'); window.location.reload(); }} variant="outline" className="rounded-xl h-11">زيارة جديدة</Button>
          <Button onClick={() => navigate('/orders/new')} variant="outline" className="rounded-xl h-11">إنشاء طلب</Button>
          <Button onClick={() => navigate('/visits')} className="bg-primary rounded-xl h-11">العودة للزيارات</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* Step indicator */}
      <div className="sticky top-0 z-10 bg-background border-b border-border px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-xl hover:bg-muted transition-colors">
            <ChevronLeft className="w-5 h-5 rtl-flip" />
          </button>
          <h1 className="text-base font-bold text-foreground flex-1">
            {step === 1 ? 'اختيار العميل' : step === 2 ? 'تسجيل الوصول' : step === 3 ? 'زيارة جارية' : 'إنهاء الزيارة'}
          </h1>
          {customer && step > 1 && (
            <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5 truncate max-w-28">{customer.name}</span>
          )}
        </div>
        {/* Progress dots */}
        <div className="flex gap-1.5">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${step > i ? 'bg-primary' : step === i + 1 ? 'bg-primary/50' : 'bg-muted'}`} />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            {step === 1 && (
              <StepSelectCustomer onSelect={handleSelectCustomer} preCustomerId={preCustomerId} preCustomerName={preCustomerName} salesmanUid={odooSession?.uid} />
            )}
            {step === 2 && customer && (
              <StepCheckIn customer={customer} onCheckIn={handleCheckIn} />
            )}
            {step === 3 && customer && (
              <StepActiveVisit customer={customer} checkInData={checkInData} visitId={visitId} onEndVisit={handleEndVisit} onAction={handleAction} activeStartTime={activeStartTime} />
            )}
            {step === 4 && customer && (
              <StepCheckOut customer={customer} duration={activeStartTime ? Math.floor((Date.now() - activeStartTime) / 1000) : 0} onConfirm={handleConfirmCheckOut} saving={saving} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}